<template>
  <div class="player-detail-actions detail-part">
    <GamMore v-bind="getMoreItems" />

    <ReportingDialog
      v-if="showReportDialog && player"
      :id="player.id"
      :type="ReportType.USERS"
      @close="showReportDialog = false"
    />
  </div>
</template>

<script lang="ts" setup>
import { ReportType } from '@/core/data/report/report.type';
import { gambits } from '@/core/gambits';
import { useAdminStore } from '@/stores/admin.store';
import { useAuthStore } from '@/stores/auth.store';
import ReportingDialog from '@/views/components/gam-dialog/dialog-contents/ReportingDialog.vue';
import GamMore from '@/views/components/gam-nav/gam-more/GamMore.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamMorePosition } from '@/views/composables/constants/components/gamMore.constants';
import type { GamMoreType } from '@/views/composables/models/components/GamMore';
import type { PlayerDetailsType } from '@/views/composables/models/main/PlayersView';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

const { player, onChange } = defineProps<PlayerDetailsType>();
const router = useRouter();
const showReportDialog = ref(false);

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);
const adminStore = useAdminStore();

const getMoreItems = computed((): GamMoreType => {
  const items = [];

  if (user.value?.id !== player.id) {
    if (user.value?.isAdmin) {
      items.push({
        label: 'app.more.delete',
        onClick: async () => {
          if (!window.confirm(`Are you sure you want to delete @${player.username}?`)) return;

          await adminStore.deleteUser(player.id);
        },
      });
    } else {
      items.push({
        label: 'app.more.report',
        onClick: () => {
          showReportDialog.value = true;
        },
      });
    }

    items.push({
      label: 'player.details.detail.actions.block.label',
      onClick: async () => {
        const message = `Are you sure you want to block @${player.username}? You will no longer be able to view each other's profiles and Gambits.`;
        if (!window.confirm(message)) return;

        const { error } = await gambits.userService.toggleBlock(player.id, player.isBlocked);
        if (error) {
          alert(error.message);
          return;
        }
        onChange?.();
        await authStore.fetchUserInfo(true);
        router.push({ path: '/profile', query: { side_panel: 'blocked' } });
      },
    });
  }

  return {
    items,
    size: GamButtonSize.SMALL,
    variant: GamButtonVariant.STEKLO,
    position: GamMorePosition.LEFT_BELOW,
    icon: GamIconName.DOTS_V,
  };
});
</script>

<style lang="scss" scoped></style>
